import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileType, AlertCircle, Download, Loader, HelpCircle, Shield, Sun, Moon, User, LogOut, MessageSquare, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { cn } from '../lib/utils';
import { useGoogleLogin } from '@react-oauth/google';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import Footer from './Footer';
import { useSubscription } from '../context/SubscriptionContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';


// Add this CSS at the top of your file
const dropzoneAnimations = `
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.02); }
  }

  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }

  @keyframes pulse-ring {
    0% { transform: scale(0.8); opacity: 0.5; }
    100% { transform: scale(1.2); opacity: 0; }
  }

  .dropzone-pulse {
    animation: pulse 2s infinite;
  }

  .upload-icon-bounce {
    animation: bounce 2s infinite;
  }

  .drag-over {
    transform: scale(1.02);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
  }

  .feature-float {
    animation: float 6s ease-in-out infinite;
  }

  .feature-icon-wrapper::before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: inherit;
    background: inherit;
    animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    opacity: 0.5;
  }
`;

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="group">
      <button
        className="w-full py-6 px-8 flex justify-between items-center text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center pr-6">
          <HelpCircle className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" />
          <span className="text-lg font-medium text-gray-900 dark:text-white">{question}</span>
        </div>
        <span 
          className={`transform transition-transform duration-200 text-gray-400 ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          ▼
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96' : 'max-h-0'
        }`}
      >
        <p className="px-8 pb-6 text-gray-600 dark:text-gray-300 leading-relaxed">
          {answer}
        </p>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon: Icon, title, description, gradient }) => (
  <div className="relative group h-full">
    {/* Background blur effect */}
    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-100 dark:to-gray-800/50 rounded-2xl blur-xl transition-opacity duration-300 opacity-0 group-hover:opacity-100" />
    
    <div className="relative h-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-8 border border-gray-200/50 dark:border-gray-700/50 shadow-lg 
      transform transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl">
      
      {/* Icon with animated background */}
      <div className="relative mb-8 feature-float">
        <div className={`w-16 h-16 mx-auto rounded-xl feature-icon-wrapper
          bg-gradient-to-br ${gradient} p-4 relative overflow-visible`}>
          <Icon className="w-full h-full text-white" />
        </div>
        
        {/* Decorative dots */}
        <div className="absolute -right-2 -top-2 w-3 h-3 rounded-full bg-blue-500/30 animate-pulse" />
        <div className="absolute -left-1 bottom-2 w-2 h-2 rounded-full bg-purple-500/30 animate-pulse delay-150" />
      </div>

      {/* Title with gradient on hover */}
      <h3 className="text-xl font-semibold mb-4 transition-colors duration-300 text-gray-900 dark:text-white
        group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-indigo-600 
        group-hover:dark:from-purple-400 group-hover:dark:to-indigo-400 
        group-hover:text-transparent group-hover:bg-clip-text">
        {title}
      </h3>

      {/* Description with fixed height and ellipsis */}
      <p className="text-gray-600 dark:text-gray-300 leading-relaxed line-clamp-3">
        {description}
      </p>

      {/* Decorative line */}
      <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent 
        w-0 group-hover:w-full transition-all duration-700 opacity-50" />
    </div>
  </div>
);

const PricingTier = ({ name, price, description, features, popular, buttonText, onClick }) => (
  <div className={cn(
    "relative rounded-2xl backdrop-blur-sm border transition-all duration-300 hover:scale-[1.02]",
    "bg-white/90 dark:bg-gray-800/90",
    popular 
      ? "border-blue-500/50 dark:border-blue-400/50 shadow-xl shadow-blue-500/10" 
      : "border-gray-200/50 dark:border-gray-700/50 shadow-lg"
  )}>
    {popular && (
      <div className="absolute -top-5 left-0 right-0 mx-auto w-32 rounded-full bg-gradient-to-r 
        from-blue-500 to-purple-500 text-white text-sm font-medium px-3 py-1 text-center">
        Most Popular
      </div>
    )}

    <div className="p-8">
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{name}</h3>
      <p className="text-gray-500 dark:text-gray-400 mb-6">{description}</p>
      
      <div className="mb-6">
        <span className="text-4xl font-bold text-gray-900 dark:text-white">${price}</span>
        {price > 0 && <span className="text-gray-500 dark:text-gray-400">/month</span>}
      </div>

      <ul className="space-y-4 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <svg className="w-5 h-5 text-blue-500 mt-1 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-gray-600 dark:text-gray-300">{feature}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={onClick}
        className={cn(
          "w-full rounded-xl px-4 py-2.5 font-medium transition-all duration-200",
          popular
            ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl"
            : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
        )}
      >
        {buttonText}
      </button>
    </div>
  </div>
);

const UserButton = ({ user, onSignOut, setShowFeedbackDialog }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className="relative rounded-full overflow-hidden 
        hover:ring-2 hover:ring-blue-500/20 dark:hover:ring-blue-500/20 
        transition-all duration-300 transform hover:scale-105
        shadow-lg hover:shadow-xl">
        {user?.imageUrl ? (
          <img 
            src={user.imageUrl} 
            alt={user.name || 'User profile'} 
            className="w-8 h-8 rounded-full cursor-pointer"
          />
        ) : (
          <div className="w-8 h-8 rounded-full 
            bg-gradient-to-br from-blue-500/10 to-purple-500/10 
            dark:from-blue-500/20 dark:to-purple-500/20
            flex items-center justify-center">
            <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </div>
        )}
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent 
      align="end" 
      className="w-56 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl 
        border border-gray-200/20 dark:border-gray-700/20 shadow-xl"
    >
      {/* User Info */}
      <div className="px-2 py-1.5 border-b border-gray-200 dark:border-gray-700">
        <div className="text-sm font-medium text-gray-900 dark:text-white">
          {user?.name || 'User'}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {user?.email}
        </div>
      </div>
      
      {/* Menu Items */}
      <DropdownMenuItem 
        className="flex items-center cursor-pointer"
        onClick={() => window.location.href = 'https://billing.stripe.com/p/login/4gw4gi41z9uneYg000'}
      >
        <Settings className="w-4 h-4 mr-2" />
        Manage Subscription
      </DropdownMenuItem>
      
      <DropdownMenuItem 
        className="flex items-center cursor-pointer"
        onClick={() => setShowFeedbackDialog(true)}
      >
        <MessageSquare className="w-4 h-4 mr-2" />
        Submit Feedback
      </DropdownMenuItem>

      <div className="h-px bg-gray-200 dark:bg-gray-700 my-1" />
      
      <DropdownMenuItem 
        className="flex items-center cursor-pointer text-red-600 dark:text-red-400"
        onClick={onSignOut}
      >
        <LogOut className="w-4 h-4 mr-2" />
        Sign Out
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const BankStatement = () => {
  const { theme, toggleTheme } = useTheme();
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [conversionUsage, setConversionUsage] = useState(null);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [user, setUser] = useState(null);
  const [planToSubscribe, setPlanToSubscribe] = useState(null);
  const [outputFormat, setOutputFormat] = useState("excel");

  const { subscriptionData } = useSubscription();
  const getPlanType = (subscriptionData) => {
    // Check if subscriptionData exists and if it contains a price_id property.
    if (!subscriptionData || !subscriptionData.price_id) return "free";
    const price_id = subscriptionData.price_id;
    if (["price_1QiyNOKDkiAkd8THwUH1ipmn", "price_1QiylaKDkiAkd8THMGU2KGeu"].includes(price_id)) {
      return "standard";
    } else if (["price_1QiyoiKDkiAkd8THcblUbZC1", "price_1Qiyp5KDkiAkd8THizvHqzXr"].includes(price_id)) {
      return "pro";
    }
    return "free";
  };
  const plan = getPlanType(subscriptionData);

  // Add this useEffect to handle initial user check
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
  }, []);

  // Debug: Log the user when it changes
  // Add this useEffect to handle subscription after user is set
  useEffect(() => {
    if (user && planToSubscribe) {
      const baseUrl = planToSubscribe === 'standard' 
        ? 'https://buy.stripe.com/cN21676HUfYK5YAaEG'
        : 'https://buy.stripe.com/aEU8yz4zM9Am1Ik003';

      const url = new URL(baseUrl);
      url.searchParams.append('client_reference_id', user.id);
      if (user.email) {
        url.searchParams.append('prefilled_email', user.email);
      }

      window.location.href = url.toString();
      setPlanToSubscribe(null); // Clear the plan after redirect
    }
  }, [user, planToSubscribe]);

  useEffect(() => {
    async function fetchUsage() {
      try {
        const response = await axios.get(`${API_URL}/bank-statement/usage`);
        setConversionUsage(response.data);
      } catch (error) {
        console.error("Error fetching conversion usage:", error);
      }
    }
    fetchUsage();
  }, [user]);

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const response = await axios.post(
          `${API_URL}/auth/google/login`,
          { token: tokenResponse.access_token },
          {
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );

        if (response.data?.access_token) {
          const userData = {
            access_token: response.data.access_token,
            token_type: response.data.token_type,
            ...response.data.user
          };
          localStorage.setItem('access_token', userData.access_token);
          localStorage.setItem('user', JSON.stringify(userData));
          setUser(userData); // This will trigger the useEffect above
        }
      } catch (error) {
        console.error('Login error:', error);
        setPlanToSubscribe(null); // Clear plan on error
      }
    },
    onError: (error) => {
      console.error('Google login failed:', error);
      setPlanToSubscribe(null); // Clear plan on error
    }
  });

  const handleSubscribe = (plan) => {
    setPlanToSubscribe(plan);
    if (!user) {
      login();
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    setError(null);
    
    // Validate each file
    const validFiles = acceptedFiles.filter(file => {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Some files were skipped. Please upload PDF or image files (JPEG, PNG, GIF, WEBP) only');
        return false;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        setError('Some files were skipped. Files must be less than 10MB');
        return false;
      }
      
      return true;
    });
    
    setFiles(prevFiles => [...prevFiles, ...validFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp']
    },
    maxFiles: 10 // Allow up to 10 files
  });

  const handleConvert = async () => {
    if (files.length === 0) return;

    setIsProcessing(true);
    setError(null);

    try {
      const formData = new FormData();
      
      // If single file, use 'file' parameter, otherwise use 'files'
      if (files.length === 1) {
        formData.append('file', files[0]);
      } else {
        files.forEach(file => {
          formData.append('files', file);
        });
      }

      const response = await axios.post(
        `${API_URL}/bank-statement/convert?output_format=${outputFormat}`,
        formData,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Handle the response based on content type
      const contentType = response.headers['content-type'];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      if (contentType === 'application/zip') {
        link.setAttribute('download', 'converted_documents.zip');
      } else {
        // Single file case
        const originalName = files[0].name;
        link.setAttribute('download', `converted_${originalName.substring(0, originalName.lastIndexOf('.'))}.${outputFormat}`);
      }
      
      document.body.appendChild(link);
      link.click();
      link.remove();

      setResult(url);
      // Clear the file selection after a successful conversion.
      setFiles([]);
    } catch (err) {
      if (err.response && err.response.status === 429) {
        setError(err.response.data.detail || "Conversion limit reached. Please consider subscribing or upgrading your plan.");
      } else {
        setError(err.response?.data?.detail || 'Error converting files');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const removeFile = (indexToRemove) => {
    setFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
  };

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": ["SoftwareApplication", "WebApplication"],
    "name": "Bank Statement Converter",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Web",
    "description": "Free online bank statement converter. Convert bank statements from PDF and images to Excel or CSV format instantly. Support for all banks and multiple formats (PDF, JPEG, PNG).",
    "keywords": "bank statement converter, convert bank statement to excel, bank statement to csv, pdf bank statement converter, bank statement excel converter, convert bank statement pdf to excel, bank statement converter online, free bank statement converter",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "1247",
      "reviewCount": "892"
    },
    "featureList": [
      "Convert bank statements to Excel/CSV",
      "Support for all major banks",
      "PDF and image format support",
      "Instant conversion",
      "Free to use",
      "Bank-grade security"
    ]
  };

  const faqItems = [
    {
      question: "What file formats are supported?",
      answer: "We support PDF files and various image formats including JPEG, PNG, GIF, and WEBP. Files must be under 10MB in size."
    },
    {
      question: "How accurate is the conversion?",
      answer: "Our AI-powered conversion system achieves high accuracy for well-formatted bank statements. The system can recognize and extract transaction details, dates, amounts, and descriptions."
    },
    {
      question: "Is my data secure?",
      answer: "Yes, we take security seriously. All uploads are encrypted, processed securely, and automatically deleted after conversion. We never store your financial documents."
    },
    {
      question: "Can I convert multiple statements at once?",
      answer: "Yes! You can upload up to 10 files simultaneously. They will be converted and delivered as a single zip file containing individual Excel files."
    },
    {
      question: "What information is extracted?",
      answer: "The converter extracts transaction dates, descriptions, credits, debits, and running balances. The Excel output maintains a format similar to your bank statement."
    }
  ];

  const handleSignOut = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    window.location.reload();
  };

  return (
    <>
      <style>{dropzoneAnimations}</style>
      <Helmet>
        <title>Bank Statement Converter to Excel & CSV | Free Online Tool</title>
        <meta name="description" content="Convert bank statements from PDF and images to Excel format instantly. Free online converter supporting PDF, JPEG, PNG, GIF, and WEBP formats." />
        <meta name="keywords" content="bank statement converter, convert bank statement to excel, bank statement to csv, pdf bank statement converter, bank statement excel converter, convert bank statement pdf to excel, bank statement converter online, free bank statement converter, image to excel, image to csv" />
        <link rel="canonical" href="https://predica.ai/bank-statement" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Bank Statement Converter to Excel & CSV | Free Online Tool" />
        <meta property="og:description" content="Free online tool to convert bank statements from PDF and images to Excel/CSV format instantly. Support for all banks and multiple formats." />
        <meta property="og:url" content="https://predica.ai/bank-statement" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bank Statement Converter to Excel & CSV | Free Online Tool" />
        <meta name="twitter:description" content="Free online tool to convert bank statements from PDF and images to Excel/CSV format instantly. Support for all banks and multiple formats." />

        {/* Additional SEO Meta Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Predica.AI" />
        <meta name="language" content="English" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-[#0D1117] transition-colors duration-300">
        {/* Update Navigation Bar */}
        <nav className="relative z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              {/* Logo */}
              <Link to="/" className="flex items-center space-x-2 group">
                <span className="text-xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400 text-transparent bg-clip-text">
                  Predica.AI
                </span>
              </Link>

              {/* Navigation Links and Buttons */}
              <div className="flex items-center space-x-4">
                {/* AI Assistant Button */}
                <Link
                  to="/"
                  className="text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white 
                    px-3 py-2 rounded-lg transition-colors duration-200 hidden sm:block"
                >
                  AI Assistant
                </Link>

                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className={cn(
                    "p-2 rounded-lg transition-colors duration-300",
                    "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white",
                    "hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
                  )}
                  aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {theme === 'dark' ? (
                    <Moon className="w-5 h-5" />
                  ) : (
                    <Sun className="w-5 h-5" />
                  )}
                </button>

                {/* Conditional render login button or user menu */}
                {user ? (
                  <div className="flex items-center space-x-4">
                    <UserButton 
                      user={user}
                      onSignOut={handleSignOut}
                      setShowFeedbackDialog={setShowFeedbackDialog}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => login()}
                    className="inline-flex items-center justify-center px-4 py-2 rounded-xl
                      bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700
                      dark:from-purple-500 dark:to-indigo-500 dark:hover:from-purple-600 dark:hover:to-indigo-600
                      text-white font-medium shadow-sm hover:shadow-lg
                      transition-all duration-200 transform hover:scale-[1.02]"
                  >
                    Login/Register
                  </button>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Animated Background Elements */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="animated-bg absolute inset-0 opacity-5" />
          <div className="mesh-grid absolute inset-0" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-50/50 to-gray-50 dark:via-[#0D1117]/50 dark:to-[#0D1117]" />
        </div>

        {/* Content wrapper */}
        <div className="relative pt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">

            {/* Hero Section with Gradient Text */}
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-6">
                <FileType className="w-4 h-4 text-blue-600 mr-2" />
                <span className="text-sm text-blue-600">Bank Statement Converter</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                <span className="block text-gray-900 dark:text-white">Bank Statement Converter</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400">
                  to Excel & CSV Format
                </span>
              </h1>
              <p className="text-xl sm:text-2xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                Free online tool to convert bank statements from any bank instantly
              </p>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Support for all major banks • Secure conversion • Instant download
              </div>
            </div>

            {/* Main Converter Section */}
            <div className="max-w-3xl mx-auto mb-24 relative">
              {/* Decorative elements */}
              <div className="absolute -top-10 -right-10 w-72 h-72 bg-purple-500/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-8 -left-8 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl" />

              {/* Converter Card */}
              <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10 border border-gray-200/50 dark:border-gray-700/50">
                {/* Dropzone */}
                <div
                  {...getRootProps()}
                  className={`relative border-3 border-dashed rounded-xl p-8 sm:p-10 text-center cursor-pointer
                    transition-all duration-300 ease-in-out transform
                    ${
                      isDragActive
                        ? 'drag-over border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 hover:scale-[1.01]'
                    }
                    group
                  `}
                >
                  <input {...getInputProps()} />
                  
                  {/* Animated background effect */}
                  <div 
                    className={`absolute inset-0 rounded-xl transition-opacity duration-300 pointer-events-none
                      ${isDragActive ? 'opacity-100' : 'opacity-0'}
                    `}
                    style={{
                      background: 'radial-gradient(circle at center, rgba(59, 130, 246, 0.1) 0%, transparent 70%)'
                    }}
                  />

                  {/* Upload icon with animation */}
                  <div className={`
                    mx-auto h-12 w-12 mb-4
                    ${isDragActive ? 'upload-icon-bounce text-blue-500' : 'text-gray-400 dark:text-gray-500 group-hover:text-blue-500'}
                    transition-colors duration-300
                  `}>
                    <Upload className="w-full h-full" />
                  </div>

                  {/* Main text */}
                  <p className={`
                    text-lg transition-colors duration-300
                    ${isDragActive 
                      ? 'text-blue-600 dark:text-blue-400' 
                      : 'text-gray-600 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100'
                    }
                  `}>
                    {isDragActive 
                      ? 'Drop your files here...' 
                      : 'Drag & drop your bank statements here, or click to select'
                    }
                  </p>

                  {/* Supported formats text */}
                  <p className={`
                    text-sm mt-2 transition-colors duration-300
                    ${isDragActive 
                      ? 'text-blue-500 dark:text-blue-400' 
                      : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'
                    }
                  `}>
                    PDF or image files (JPEG, PNG, GIF, WEBP), max 10MB each
                  </p>

                  {/* Optional: Add a subtle shine effect on drag */}
                  <div 
                    className={`
                      absolute inset-0 pointer-events-none transition-opacity duration-500
                      ${isDragActive ? 'opacity-100' : 'opacity-0'}
                    `}
                    style={{
                      background: 'linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)',
                      backgroundSize: '200% 200%',
                      animation: isDragActive ? 'shine 1.5s infinite' : 'none'
                    }}
                  />
                </div>

                {/* Daily Conversions Counter with improved styling */}
                <div className="mt-4 flex items-center justify-center">
                  <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-100 dark:border-blue-800/30">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 animate-pulse" />
                      <span className="text-sm font-medium bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
                        {conversionUsage ? (
                          plan === "free" ?
                          `${conversionUsage.conversion_count} / ${conversionUsage.conversion_limit} conversions today` :
                          `${conversionUsage.conversion_count} / ${conversionUsage.conversion_limit} conversions this month`
                        ) : "Loading usage..."}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Output Format Switch */}
                <div className="mt-4 flex items-center justify-center">
                  <div className="inline-flex items-center p-1 rounded-full 
                    bg-gradient-to-br from-gray-100/50 to-gray-200/50 
                    dark:from-gray-800/50 dark:to-gray-900/50 
                    backdrop-blur-sm border border-gray-200/20 dark:border-gray-700/20 
                    shadow-lg shadow-gray-500/5">
                    <div
                      onClick={() => setOutputFormat("excel")}
                      className={`px-6 py-1.5 rounded-full text-sm font-medium 
                        transition-all duration-300 ease-in-out transform 
                        hover:shadow-md relative z-10 ${
                        outputFormat === "excel"
                          ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                          : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                        }`}
                    >
                      Excel
                    </div>
                    <div
                      onClick={() => setOutputFormat("csv")}
                      className={`px-6 py-1.5 rounded-full text-sm font-medium 
                        transition-all duration-300 ease-in-out transform 
                        hover:shadow-md relative z-10 ${
                        outputFormat === "csv"
                          ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                          : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                        }`}
                    >
                      CSV
                    </div>
                  </div>
                </div>

                {/* File list with improved styling */}
                {files.length > 0 && (
                  <div className="mt-6 space-y-3">
                    {files.map((file, index) => (
                      <div 
                        key={index} 
                        className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200/50 dark:border-gray-600/50 backdrop-blur-sm
                          transform transition-all duration-300 hover:scale-[1.02]"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                              <FileType className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <span className="text-gray-700 dark:text-gray-300 font-medium">{file.name}</span>
                          </div>
                          <button
                            onClick={() => removeFile(index)}
                            className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50
                              transition-colors duration-200"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {/* Convert button with gradient */}
                    <button
                      onClick={handleConvert}
                      disabled={isProcessing}
                      className={`w-full mt-6 px-6 py-3 rounded-xl font-medium text-white shadow-lg
                        transform transition-all duration-300 hover:scale-[1.02]
                        ${isProcessing
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                        }`}
                    >
                      {isProcessing ? (
                        <div className="flex items-center justify-center space-x-2">
                          <Loader className="w-5 h-5 animate-spin" />
                          <span>Converting files...</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-2">
                          <Download className="w-5 h-5" />
                          <span>
                            Convert {files.length} file{files.length > 1 ? 's' : ''} to {outputFormat.toUpperCase()}
                          </span>
                        </div>
                      )}
                    </button>
                  </div>
                )}

                {/* Error message with improved styling */}
                {error && (
                  <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-xl border border-red-100 dark:border-red-800/30 backdrop-blur-sm
                    flex items-center space-x-3">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{error}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Features Section with gradient cards */}
            <div className="max-w-6xl mx-auto mb-24">
              <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">
                <span className="bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400 text-transparent bg-clip-text">
                  Why Choose Our Converter?
                </span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 px-4">
                <FeatureCard
                  icon={Shield}
                  title="Bank-Grade Security"
                  description="Your documents are encrypted and automatically deleted after processing. We prioritize the security of your financial data."
                  gradient="from-emerald-500 to-teal-500"
                />
                <FeatureCard
                  icon={Download}
                  title="Lightning Fast"
                  description="Experience instant conversions with our optimized OCR engine. Get your Excel files ready in seconds."
                  gradient="from-blue-500 to-indigo-500"
                />
                <FeatureCard
                  icon={FileType}
                  title="Universal Compatibility"
                  description="Support for all major formats including PDF, JPEG, PNG, GIF, and WEBP. Convert multiple files simultaneously."
                  gradient="from-purple-500 to-pink-500"
                />
              </div>
            </div>

            {/* Pricing Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
              <h2 className="text-3xl sm:text-4xl font-bold text-center mb-6">
                <span className="bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400 text-transparent bg-clip-text">
                  Choose Your Plan
                </span>
              </h2>
              <p className="text-gray-600 dark:text-gray-300 text-center mb-12 max-w-2xl mx-auto">
                Select the perfect plan for your needs. All plans include our core conversion features with bank-grade security.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
                <PricingTier
                  name="Free"
                  price="0"
                  description="Perfect for occasional use"
                  features={[
                    "3 conversions per day",
                    "PDF & Image support",
                    "Basic Excel output",
                    "Standard OCR accuracy",
                  ]}
                />

                <PricingTier
                  name="Standard"
                  price="14.99"
                  description="For regular business use"
                  popular={true}
                  features={[
                    "600 conversions per month",
                    "Everything in Free, plus:",
                    "AI Document Assistant access",
                    "Email support"
                  ]}
                  buttonText="Subscribe"
                  onClick={() => handleSubscribe('standard')}
                />

                <PricingTier
                  name="Pro"
                  price="29.99"
                  description="For power users & teams"
                  features={[
                    "2000 conversions per month",
                    "Everything in Standard, plus:",
                    "AI Assistant Pro features",
                    "Premium OCR (99% accuracy)",
                  ]}
                  buttonText="Subscribe"
                  onClick={() => handleSubscribe('pro')}
                />
              </div>

              <div className="mt-12 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Need a custom plan? <a href="mailto:andrew@predica.ai" className="text-blue-500 hover:text-blue-600">Contact our sales team</a>
                </p>
              </div>
            </div>

            {/* FAQ Section with improved styling */}
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">
                <span className="bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400 text-transparent bg-clip-text">
                  Frequently Asked Questions
                </span>
              </h2>
              <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden
                border border-gray-200/50 dark:border-gray-700/50">
                {faqItems.map((item, index) => (
                  <div key={index} className="border-b border-gray-200 dark:border-gray-700 last:border-0">
                    <FAQItem
                      question={item.question}
                      answer={item.answer}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Add Footer */}
        <Footer />
      </div>
    </>
  );
};

export default BankStatement; 